import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface CreateZoneDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; region: string; price_per_kg: number }) => Promise<void>;
}

interface CreateZoneFormData {
  name: string;
  region: string;
  price_per_kg: number;
}

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Zone name is required')
    .min(2, 'Zone name must be at least 2 characters')
    .max(100, 'Zone name must be at most 100 characters'),
  region: yup
    .string()
    .required('Region is required'),
  price_per_kg: yup
    .number()
    .typeError('Price must be a number')
    .required('Price per kg is required')
    .positive('Price must be greater than 0')
    .max(100000, 'Price must be reasonable'),
});

const CreateZoneDialog: React.FC<CreateZoneDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, handleSubmit, formState: { errors }, reset } = useForm<CreateZoneFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      region: '',
      price_per_kg: 0,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (data: CreateZoneFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      reset();
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  // Predefined regions for Burkina Faso delivery app
  const regions = [
    { value: 'National', label: t('zones.regions.national') },
    { value: 'West Africa', label: t('zones.regions.westAfrica') },
    { value: 'Central Africa', label: t('zones.regions.centralAfrica') },
    { value: 'International', label: t('zones.regions.international') },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('zones.createNew')}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} pt={1}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('zones.fields.name')}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  placeholder={t('zones.placeholders.name')}
                  disabled={isSubmitting}
                />
              )}
            />

            <Controller
              name="region"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.region}>
                  <InputLabel>{t('zones.fields.region')}</InputLabel>
                  <Select
                    {...field}
                    label={t('zones.fields.region')}
                    disabled={isSubmitting}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region.value} value={region.value}>
                        {region.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.region && (
                    <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                      {errors.region.message}
                    </Box>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="price_per_kg"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('zones.fields.pricePerKg')}
                  type="number"
                  fullWidth
                  error={!!errors.price_per_kg}
                  helperText={errors.price_per_kg?.message}
                  placeholder="0"
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">XOF</InputAdornment>,
                    inputProps: { min: 0, step: 50 }
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting ? t('common.creating') : t('common.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateZoneDialog;
